/**
 * **オブジェクトの器**（Issue #523）。フィールド・HUD に並ぶ 3 つ目の描画層。
 *
 * ## 何を持つか（ユーザー判断 2026-09-24）
 *
 * **「ocgCore の状態の描画」と「何を表示するかの設定」**。だから受け取るのは
 * *置くものの一覧* ではなく**盤面の状態**（`core/board`）で、**何を描くかはここが決める**。
 *
 * | 種類 | 何 | どこから | 置き場（`OBJECT_KINDS` の `space`） |
 * |---|---|---|---|
 * | `card` | 場のカード（モンスター・魔法罠・フィールド魔法） | 盤面の 1 枚ぶんの場所 | 盤の上 |
 * | `pile` | 山（デッキ・墓地・除外・EX・手札）。**一番上と枚数**にするのはここの判断 | 盤面の山 | 盤の上 |
 * | `frame` | **盤の縁**（ターンプレイヤーの色で光る） | そのフレームの HUD の値 | 舞台のピクセル |
 * | `liveHand` | **実写っぽい手札**（合成フィールド動画の両端の帯。平面の外に落ちている部分） | 元動画 | 舞台のピクセル |
 *
 * **動きは最小**（状態をそのまま描く）。時間で動くもの＝演出は別の層（`effects`）。
 *
 * ## 置き方は器・見た目はテーマ（Issue #523）
 *
 * 盤に**置かれている**ものは板なので、**厚み・浮き・影**がある（デッキの厚み、少し浮いたカード）。
 * これは three でしか出せないが、**テーマに three を持たせたくない**（見た目は 2D の絵で書けるほうが
 * 書きやすいし、`boundary.test.js` の「themes は core と canvas と react だけ」を壊したくない）。
 *
 * そこで分けた:
 *
 * - **テーマ**は `(env) => { draw(ctx), lift, thickness, … }` を返す**純関数**。板の**上面の絵**を
 *   2D canvas で描くだけで、どこに置かれるかも三次元も知らない
 * - **器**（ここ）がゾーンのキーから場所（平面の座標）・大きさ・守備表示の回しを決め、
 *   `stage/three/runtime.js` の `piece`（盤の上）/ `panel`（舞台のピクセル）に渡す
 */
import { useMemo } from 'react';
import { CARD_ASPECT, parseZoneKey, ROW_KINDS } from '../core/field/zones';
import { MARKS } from '../core/mark';
import { OBJECT_KINDS, resolveObjects, resolveStyle } from '../core/theme';
import { placeBox, transformOf } from '../core/transform';
import ThreeStage from './ThreeStage';
import { withSplit } from './split';

/** 板の絵のビットマップの倍率（平面のピクセルの何倍で描くか） */
export const DEFAULT_OBJECT_RESOLUTION = 2;

/** 1 枚ぶんの場所（それ以外は山） */
const isSingle = (kind) => ROW_KINDS.includes(kind) || kind === 'field';

/*
 * **板の id**（外から `tweak` を差すときの鍵）。演出は uid で考えるので、
 * `core/board` の `byUid(uid) → { zone }` から引いてここに通す。
 */
export const cardObjectId = (zone) => (zone ? `card:${zone}` : null);
export const pileObjectId = (zone) => (zone ? `pile:${zone}` : null);

const clamp01 = (v) => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/**
 * **状態 ＋ 設定 → 何を描くか**（純関数）。
 *
 * @param board `core/board` の盤面
 * @param scene そのフレームの素材（`liveHand` は元動画から出す）
 * @returns `[{ id, kind, zone, card, top, count, player }]`
 */
export function objectItems(board, scene = null) {
  const out = [];
  (board?.keys ?? []).forEach((zone) => {
    const at = parseZoneKey(zone);
    if (!at) return;
    if (isSingle(at.kind)) {
      const card = board.at(zone);
      if (card) out.push({ id: `card:${zone}`, kind: 'card', zone, card, player: at.player });
      return;
    }
    const count = board.count(zone);
    if (!count) return;
    const pile = board.pile(zone);
    // **一番上と枚数だけ**にするのはここの判断（中身が要るようになったら pile をそのまま渡す）
    out.push({ id: `pile:${zone}`, kind: 'pile', zone, top: pile[pile.length - 1] ?? null, count, player: at.player });
  });
  // 盤の縁は盤面ではなく**そのフレームの HUD の値**から（ターンプレイヤー）
  if (scene?.hud) {
    out.push({ id: 'frame', kind: 'frame', player: scene.hud.turnPlayer ?? 0, turn: scene.hud.turn ?? 0 });
  }
  // 実写っぽい手札は盤面ではなく元動画から（両端の帯）
  if (scene?.video?.el && scene?.video?.size?.w > 0) {
    [0, 1].forEach((player) => out.push({ id: `liveHand:${player}`, kind: 'liveHand', player }));
  }
  return out;
}

/**
 * **その項目の中身**（＝これが変わったら板の絵を描き直す）。
 *
 * 項目はフレームごとに作り直されるので、**同一性ではなく値**で見る
 * （`core/board` の盤面も毎フレーム作り直される）。
 */
const itemDeps = (item) => {
  if (item.kind === 'card') return [item.zone, item.card.uid, item.card.code, item.card.faceUp, item.card.defense];
  if (item.kind === 'pile') return [item.zone, item.count, item.top?.uid, item.top?.code, item.top?.faceUp];
  if (item.kind === 'frame') return [item.player, item.turn];
  return [item.player];
};

/**
 * **カードの状態による盤の上での回し**（ラジアン）。盤の敷き方の分（Field の `cardAngle`）に足す。
 *
 * | 状態 | 回し |
 * |---|---|
 * | 守備表示のモンスター | +90° |
 * | **セットした魔法・罠**（裏の `magic_trap` / `field`） | **+180°**（ユーザー判断 2026-09-24。手札から伏せるので向きが逆） |
 * | それ以外 | 0 |
 *
 * 山（`pile`）はカードの状態を持たないので 0。
 */
export function cardTurn(item) {
  const card = item?.card;
  if (!card) return 0;
  if (card.defense) return Math.PI / 2;
  const kind = parseZoneKey(item.zone)?.kind;
  if (!card.faceUp && (kind === 'magic_trap' || kind === 'field')) return Math.PI;
  return 0;
}

/** ゾーンの中心（平面の座標）。写らないゾーンは null */
function zoneCenter(view, zone) {
  const quad = view?.zonePlane?.(zone);
  if (!Array.isArray(quad) || quad.length !== 4) return null;
  const cx = quad.reduce((a, p) => a + p[0], 0) / 4;
  const cy = quad.reduce((a, p) => a + p[1], 0) / 4;
  return Number.isFinite(cx) && Number.isFinite(cy) ? [cx, cy] : null;
}

/**
 * **演出が置いたもの**（Issue #534）→ オブジェクトの項目。
 *
 * 盤面の状態から出るもの（`objectItems`）と**同じ一覧に並ぶ** —— 生存も描画も
 * オブジェクトレイヤーが持つので、帯が終わっても消えない（設計文書 §4）。
 *
 * `at` は `{ zone }` か `{ card }`（uid。盤面を引いてゾーンへ写す）。引けないものは落とす。
 */
export function fxItems(placed = [], board = null) {
  return (placed ?? []).map((span) => {
    const at = span?.data?.at ?? (span?.key ? { card: span.key } : null);
    let zone = at?.zone ?? null;
    if (!zone && at?.card) zone = board?.byUid?.(at.card)?.zone ?? null;
    if (!zone) return null;
    return {
      id: span.id,
      kind: span.mark,
      source: 'fx',
      zone,
      card: board?.at?.(zone) ?? null,
      player: /:1(?::|$)/.test(zone) ? 1 : 0,
      data: span.data ?? null,
      /** 置かれた時刻（印の描き方が「置かれてからの時間」で動くとき。Issue #571） */
      startMs: Number.isFinite(span.startMs) ? span.startMs : null,
    };
  }).filter(Boolean);
}

const bitmapFor = (w, h, res) => ({ w: Math.max(1, Math.round(w * res)), h: Math.max(1, Math.round(h * res)) });

/**
 * **盤面の状態 ＋ 設定 ＋ テーマ → three に渡す `parts`**（純関数）。
 *
 * 種類の順は `OBJECT_KINDS` の宣言順（手前に重なる順）。**テーマが描き方を持たない種類・
 * 濃さ 0・spec を返さなかった項目**は出ない。
 */
export function objectParts(theme, scene, view, canvas, config = null, tweaks = null) {
  const cfg = config ?? {};
  const opacity = resolveObjects(cfg.objects);
  const res = cfg.objectResolution > 0 ? cfg.objectResolution : DEFAULT_OBJECT_RESOLUTION;
  const options = { ...(theme?.options ?? {}), ...cfg };
  // **テーマの見た目の値**（Issue #523。宣言は `theme.schema`。canvas の層なので値で読む）
  const style = resolveStyle(theme, cfg.style);
  /*
   * **盤面の状態から出るもの ∪ 演出が置いたもの**（Issue #534）。
   * 種類の描き方はどちらもテーマが持つ（`theme.objects[kind]`）ので、扱いは同じ。
   */
  const items = [...objectItems(scene?.board, scene), ...fxItems(scene?.fxObjects, scene?.board)];
  const size = { w: view?.unit ?? 0, h: (view?.unit ?? 0) / CARD_ASPECT };
  const out = [];

  /** 盤面の 4 種 ＋ **演出が置いた印**（`core/mark` の語彙）。どちらもテーマが描き方を持つ */
  const kinds = [
    ...OBJECT_KINDS,
    ...MARKS.filter((m) => items.some((i) => i.kind === m.name)).map((m) => ({ ...m, presets: null })),
  ];
  kinds.forEach((kind) => {
    const make = theme?.objects?.[kind.name];
    // 印は濃さの設定を持たない（盤面の 4 種だけが presets を持つ）
    const alpha = kind.presets === null ? 1 : clamp01(opacity[kind.name]);
    if (typeof make !== 'function' || alpha <= 0) return;
    items.filter((item) => item.kind === kind.name).forEach((item) => {
      const spec = make({ item, scene, view, canvas, size, options, style });
      if (!spec || typeof spec.draw !== 'function') return;
      const tweak = tweaks?.[item.id];
      // **種類ごとのトランスフォーム**（Issue #523。`objects:card` などの部品に付く共通のパラメータ）
      const tf = transformOf(style, `objects:${kind.name}`);
      const common = {
        id: item.id,
        kindName: kind.name,
        opacity: alpha * tf.opacity,
        draw: spec.draw,
        // **中身は見ない**（生の three のオブジェクトを触る関数をそのまま runtime へ）
        tweak: typeof tweak === 'function' ? tweak : undefined,
        // 書体が読めたら描き直す（Issue #549）
        // **渡されたもので見る**（`options` / `style` は毎回作り直すので、それで見ると全部の板が毎フレーム描き直しになる。Issue #641）
        deps: [theme, config, config?.style, scene?.fontReady, ...itemDeps(item), ...(spec.deps ?? [])],
      };
      if (kind.space === 'screen') {
        const box = spec.box;
        if (!box || !(box.w > 0) || !(box.h > 0)) return;
        const t = placeBox(box, tf, 'screen');
        out.push({
          ...common,
          kind: 'panel',
          box: { x: t.center[0] - box.w / 2, y: t.center[1] - box.h / 2, w: box.w, h: box.h },
          scale: t.scale,
          rot: t.rot,
          bitmap: spec.bitmap ?? bitmapFor(box.w, box.h, 1),
        });
        return;
      }
      const at = zoneCenter(view, item.zone);
      if (!at || !(size.w > 0)) return;
      const t = placeBox({ x: at[0] - size.w / 2, y: at[1] - size.h / 2, w: size.w, h: size.h }, tf, 'board');
      out.push({
        ...common,
        kind: 'piece',
        center: t.center,
        size,
        scale: t.scale,
        /*
         * **盤の上での向き**（舞台のピクセルで回すと、遠近が付いた後の形を回すことになる）。
         * 盤の敷き方の分は Field、カードの状態の分は `cardTurn`。
         */
        rot: (view?.cardAngle?.(item.zone) ?? 0) + cardTurn(item) + t.rot,
        lift: spec.lift ?? 0,
        thickness: spec.thickness,
        shadow: spec.shadow,
        edgeColor: spec.edgeColor,
        bitmap: spec.bitmap ?? bitmapFor(size.w, size.h, res),
      });
    });
  });
  return out;
}

/**
 * @param theme `core/theme::toTheme()` を通したテーマ
 * @param scene そのフレームの素材（`scene.board` が盤面の状態）
 * @param view 盤の見え方（カメラと場所）
 * @param canvas 舞台の大きさ
 * @param config 設定（`config.objects` が種類ごとの濃さ）
 * @param tweaks 板の id → 生のオブジェクトを触る関数（外から差す。**そのフレームぶん**）
 * @param split 画面を切ってずらす（Issue #553）。演出の層と同じ値
 */
export default function ObjectLayer({ theme, scene, view, canvas, config = null, tweaks = null, split = null }) {
  const parts = useMemo(
    () => objectParts(theme, scene, view, canvas, config, tweaks),
    [theme, scene, view, canvas, config, tweaks],
  );
  if (!parts.length) return null;
  const kinds = OBJECT_KINDS.map((k) => k.name).filter((name) => parts.some((p) => p.kindName === name));
  return (
    <div className="de-layer" data-layer="objects" data-kinds={kinds.join(',')}>
      <ThreeStage canvas={canvas} shot={view.shot} plane={view.plane} parts={parts} post={withSplit(null, split)} testId="de-objects-gl" scale={config?.renderScale ?? 1} />
    </div>
  );
}
